<template>
  <div class="container mt-5">
    <div class="about">
      <h1 class="text-center mb-4">Image Upload Page</h1>

      <!-- Drag and Drop Upload Area -->
      <div
          class="border border-primary p-3 mb-4 text-center"
          @dragover.prevent
          @drop.prevent="handleDrop"
          :class="{ 'border-success': isDragging }"
          @dragenter="isDragging = true"
          @dragleave="isDragging = false"
      >
        <p class="mb-2">Drag & Drop your images here, or click to select files</p>
        <input
            type="file"
            multiple
            ref="fileInput"
            class="d-none"
            @change="handleImageUpload"
        />
        <button class="btn btn-primary" @click="openFilePicker">Choose Files</button>
      </div>

      <!-- Search Box -->
      <div class="input-group mb-4">
        <input
            type="text"
            class="form-control"
            placeholder="Search images by name..."
            v-model="searchQuery"
        />
      </div>

      <!-- Uploaded Images List -->
      <div class="row">
        <div
            class="col-sm-6 col-md-4 col-lg-3 mb-4"
            v-for="image in filteredImages"
            :key="image.id"
        >
          <div class="card" @click="copyImageURL(image.url)">
            <img :src="image.url" class="card-img-top" :alt="image.name"/>
            <div class="card-body">
              <h5 class="card-title">{{ image.name }}</h5>
              <p class="card-text">
                <strong>Type:</strong> {{ image.type }}<br/>
                <strong>Size:</strong> {{ formatSize(image.size) }}
              </p>
            </div>
            <!-- Delete Button -->
            <button
                class="btn btn-danger position-absolute top-0 end-0 m-2"
                @click="deleteImage(image.id)"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
      <div v-if="showToast" class="toast-container position-fixed bottom-0 end-0 p-3">
        <div class="toast show align-items-center text-white bg-success border-0">
          <div class="d-flex">
            <div class="toast-body">
              Image URL copied to clipboard!
            </div>
            <button type="button" class="btn-close btn-close-white me-2 m-auto" @click="showToast = false"></button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      images: [], // Array to store uploaded images
      searchQuery: '', // Search query for filtering images
      isDragging: false, // Flag for drag-and-drop UI feedback
      showToast: false // Flag to control toast visibility
    };
  },
  computed: {
    filteredImages() {
      // Filter images based on the search query
      return this.images.filter(image =>
          image.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }
  },
  methods: {
    async fetchImages() {
      try {
        // Fetch images from the backend API
        const response = await axios.get('/dashboard/images/');
        const fetchedImages = response.data;

        // Process each image to fetch its type and size
        this.images = await Promise.all(
            fetchedImages.map(async (img) => {
              try {
                // Fetch the image as a Blob
                const res = await fetch(img.image);
                const blob = await res.blob();

                return {
                  id: img.id,
                  name: img.caption || 'Untitled',
                  url: img.image,
                  type: blob.type || 'Unknown', // Blob provides MIME type
                  size: blob.size || 0 // Blob provides size in bytes
                };
              } catch (err) {
                console.error(`Error processing image ${img.image}:`, err);
                // Fallback if fetching Blob fails
                return {
                  id: img.id,
                  name: img.caption || 'Untitled',
                  url: img.image,
                  type: 'Unknown',
                  size: 0
                };
              }
            })
        );
      } catch (error) {
        console.error("Error fetching images:", error);
      }
    },
    async deleteImage(imageId) {
      try {
        // Make an API call to delete the image
        const response = await fetch(`/dashboard/delete-image/${imageId}/`, {
          method: "GET",
        });

        if (!response.ok) {
          throw new Error("Failed to delete the image.");
        }

        // Remove the image from the local list after successful deletion
        this.images = this.images.filter((image) => image.id !== imageId);
      } catch (error) {
        console.error(error.message);
        alert("Failed to delete the image. Please try again.");
      }
    },
    openFilePicker() {
      // Open the hidden file input for choosing files
      this.$refs.fileInput.click();
    },
    handleImageUpload(event) {
      const files = event.target.files || event.dataTransfer.files;
      this.processFiles(files);
    },
    handleDrop(event) {
      // Handle drop event for drag-and-drop file upload
      this.isDragging = false;
      const files = event.dataTransfer.files;
      this.processFiles(files);
    },
    async processFiles(files) {
      Array.from(files).forEach(async (file) => {
        const formData = new FormData();

        // Extract file name without extension
        const fileName = file.name.split('.').slice(0, -1).join('.');
        formData.append('image', file);
        formData.append('caption', fileName);

        try {
          const response = await axios.post('/dashboard/images/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          // Add the new image to the images list with ID, caption, URL, type, and size
          this.images.unshift({
            id: response.data.id,
            name: response.data.caption,
            url: response.data.image,
            type: file.type, // File MIME type (e.g., image/jpeg)
            size: file.size, // File size in bytes
          });
        } catch (error) {
          console.error('Error uploading image:', error);
        }
      });
    },
    formatSize(size) {
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    },

    copyImageURL(imageUrl) {
      // Construct the full URL (including protocol and domain)
      const fullURL = `${window.location.origin}${imageUrl}`;

      // Copy to clipboard
      navigator.clipboard.writeText(fullURL)
          .then(() => {
            // Show toast notification
            this.showToast = true;

            // Hide toast after 3 seconds
            setTimeout(() => {
              this.showToast = false;
            }, 3000);
          })
          .catch(err => {
            console.error("Failed to copy image URL:", err);
          });
    }

  },
  async mounted() {
    // Fetch images when component mounts
    await this.fetchImages();
  }
};
</script>


<style scoped>
.toast-container {
  z-index: 1055;
}

.about {
  max-width: 800px;
  margin: auto;
}

.border-success {
  border-color: #28a745 !important;
}

.card {
  border: none;
}

.card-title {
  font-size: 1rem;
  text-align: center;
}
</style>
